import {
  useState,
  useRef,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from "react";
import { styled } from "@mui/material/styles";
import { useParams, useNavigate } from "react-router-dom";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepButton from "@mui/material/StepButton";
import StepConnector, {
  stepConnectorClasses,
} from "@mui/material/StepConnector";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Tooltip from "@mui/material/Tooltip";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import { Context } from "../../components/context";
import { AppContext } from "../../models/applicationState";
import { Meeting, MeetingStatus } from "../../models/meeting";
import { MeetingItem } from "../../models/meetingItem";
import { PersonalNote } from "../../models/personalNote";
import SelectMeetingStep from "../../components/meetingNotes/steps/SelectMeetingStep";
import TakeNotesStep, {
  TakeNotesStepHandle,
} from "../../components/meetingNotes/steps/TakeNotesStep";
import ReviewStep from "../../components/meetingNotes/steps/ReviewStep";
import DistributeStep from "../../components/meetingNotes/steps/DistributeStep";
import { useTranslation } from "react-i18next";
import * as listActions from "../../actions/listActions";
import * as itemActions from "../../actions/itemActions";
import { ListActions } from "../../actions/listActions";
import { ItemActions } from "../../actions/itemActions";
import { bindActionCreators } from "../../actions/actionCreators";
import config from "../../config";
import { createPersonalNoteService } from "../../services/personalNoteService";
import {
  formatLocalTimeDisplay,
  hasMeaningfulTime,
  parseLocalDateValue,
  toLocalDateInputValue,
} from "../../util/meetingDateTime";

/**
 * SelectedMeeting interface for workflow display purposes
 * Maps from Pinnacle Meeting model to workflow display format
 */
export interface SelectedMeeting {
  id: string;
  title: string;
  date: string;
  startTime: string;
  endTime: string;
  attendees: number;
  originalMeeting?: Meeting;
}

const hiddenScrollbarSx = {
  scrollbarWidth: "none" as const,
  msOverflowStyle: "none" as const,
  "&::-webkit-scrollbar": {
    display: "none",
  },
};

const WorkflowConnector = styled(StepConnector)(() => ({
  [`& .${stepConnectorClasses.line}`]: {
    position: "relative",
    height: 4,
    border: 0,
    borderRadius: 999,
    backgroundColor: "#E5E7EB",
    overflow: "hidden",
  },
  [`& .${stepConnectorClasses.line}::after`]: {
    content: '""',
    position: "absolute",
    inset: 0,
    borderRadius: 999,
    background: "linear-gradient(90deg, #242C63 0%, #4C5AA8 55%, #5965a3 100%)",
    transform: "scaleX(0)",
    transformOrigin: "left center",
    transition: "transform 420ms cubic-bezier(0.22, 1, 0.36, 1)",
  },
  [`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}::after`]: {
    transform: "scaleX(1)",
  },
  [`&.${stepConnectorClasses.completed} .${stepConnectorClasses.line}::after`]:
  {
    transform: "scaleX(1)",
  },
}));

/**
 * Convert Pinnacle Meeting to SelectedMeeting display format
 */
const meetingToSelected = (meeting: Meeting): SelectedMeeting => {
  const dateStr =
    toLocalDateInputValue(meeting.date) || toLocalDateInputValue(new Date());
  const derivedStartTime =
    meeting.startTime ||
    (meeting.eventId && hasMeaningfulTime(meeting.date)
      ? formatLocalTimeDisplay(meeting.date)
      : "");

  return {
    id: meeting.id || "",
    title: meeting.name,
    date: dateStr,
    startTime: derivedStartTime,
    endTime: meeting.endTime || "",
    attendees: meeting.participants?.length || 0,
    originalMeeting: meeting,
  };
};

/**
 * Convert SelectedMeeting back to Meeting for saving
 * Reserved for future use when saving workflow changes
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _selectedToMeeting = (selected: SelectedMeeting): Meeting => {
  const base = selected.originalMeeting || { name: selected.title };
  return {
    ...base,
    id: selected.id,
    name: selected.title,
    date: parseLocalDateValue(selected.date),
    startTime: selected.startTime || undefined,
    endTime: selected.endTime || undefined,
  };
};

const didMeetingFieldChange = (currentValue: unknown, nextValue: unknown) => {
  if (currentValue instanceof Date && nextValue instanceof Date) {
    return currentValue.getTime() !== nextValue.getTime();
  }

  if (Array.isArray(currentValue) && Array.isArray(nextValue)) {
    return (
      currentValue.length !== nextValue.length ||
      currentValue.some((value, index) => value !== nextValue[index])
    );
  }

  return currentValue !== nextValue;
};

const hasMeetingUpdateChanges = (
  meeting: Meeting,
  updates?: Partial<Meeting>,
) =>
  Object.entries(updates ?? {}).some(([key, value]) =>
    didMeetingFieldChange(meeting[key as keyof Meeting], value),
  );

const MeetingWorkflow = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { meetingId } = useParams<{ meetingId?: string }>();
  const { state, dispatch } = useContext(Context) as AppContext;

  const actions = useMemo(
    () => ({
      lists: bindActionCreators(
        listActions,
        dispatch,
      ) as unknown as ListActions,
      items: bindActionCreators(
        itemActions,
        dispatch,
      ) as unknown as ItemActions,
    }),
    [dispatch],
  );
  const personalNoteService = useMemo(
    () => createPersonalNoteService(config.api.baseUrl),
    [],
  );

  const [activeStep, setActiveStep] = useState(0);
  const [items, setItems] = useState<MeetingItem[]>([]);
  const [personalNotes, setPersonalNotes] = useState<PersonalNote[]>([]);
  const [selectedMeeting, setSelectedMeeting] =
    useState<SelectedMeeting | null>(null);
  const distributeRef = useRef<(() => void) | null>(null);
  const pendingStepRef = useRef<number | null>(null);
  const takeNotesRef = useRef<TakeNotesStepHandle>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "error";
  }>({
    open: false,
    message: "",
    severity: "error",
  });

  const toPersonalNotesArray = useCallback(
    (value: unknown): PersonalNote[] => (Array.isArray(value) ? value : []),
    [],
  );

  const safePersonalNotes = useMemo(
    () => toPersonalNotesArray(personalNotes),
    [personalNotes, toPersonalNotesArray],
  );
  const steps = [
    t("meetingNotes.workflow.steps.selectMeeting"),
    t("meetingNotes.workflow.steps.takeNotes"),
    t("meetingNotes.workflow.steps.review"),
    t("meetingNotes.workflow.steps.distributeAndClose"),
  ];
  const arrowLabels: Record<number, string> = {
    0: t("meetingNotes.workflow.arrowLabels.takeNotes"),
    1: t("meetingNotes.workflow.arrowLabels.review"),
    2: t("meetingNotes.workflow.arrowLabels.distribute"),
    3: t("meetingNotes.workflow.arrowLabels.distributeAndClose"),
  };

  const handleWorkflowError = useCallback(
    (message: string, error?: unknown) => {
      console.error(message, error);
      setSnackbar({
        open: true,
        message,
        severity: "error",
      });
    },
    [],
  );

  const selectableMeetings = useMemo(
    () =>
      (state.lists || []).filter(
        (meeting) => !meeting.archived && meeting.status !== "published",
      ),
    [state.lists],
  );

  const selectFirstMeeting = useCallback(() => {
    const firstMeeting = selectableMeetings[0];
    setSelectedMeeting(firstMeeting ? meetingToSelected(firstMeeting) : null);
  }, [selectableMeetings]);

  const saveMeetingAndSync = useCallback(
    async (meeting: Meeting) => {
      const savedMeeting = await actions.lists.save(meeting);
      void actions.lists.select(savedMeeting);
      setSelectedMeeting((currentMeeting) =>
        currentMeeting?.id === savedMeeting.id
          ? meetingToSelected(savedMeeting)
          : currentMeeting,
      );
      return savedMeeting;
    },
    [actions.lists],
  );

  const handleMeetingStatusChange = useCallback(
    async (status: MeetingStatus, updates?: Partial<Meeting>) => {
      const meetingIdToUpdate = selectedMeeting?.id;
      if (!meetingIdToUpdate) {
        return false;
      }

      // First, try to find the meeting in state.lists (most up-to-date source)
      const baseMeeting = state.lists?.find(
        (candidate) => candidate.id === meetingIdToUpdate,
      );

      // If not found in state.lists, fall back to originalMeeting only if IDs match
      if (!baseMeeting) {
        if (selectedMeeting.originalMeeting?.id !== meetingIdToUpdate) {
          handleWorkflowError(
            t("meetingNotes.workflow.errors.meetingNotFoundRefresh"),
          );
          return false;
        }
      }

      const meetingToUse =
        baseMeeting ||
        (selectedMeeting.originalMeeting?.id === meetingIdToUpdate
          ? selectedMeeting.originalMeeting
          : null);

      if (!meetingToUse) {
        return false;
      }

      if (
        meetingToUse.status === status &&
        !hasMeetingUpdateChanges(meetingToUse, updates)
      ) {
        return true;
      }

      try {
        const meetingToSave = {
          ...meetingToUse,
          ...updates,
          status,
          updatedDate: new Date(),
        };
        await saveMeetingAndSync(meetingToSave);
        return true;
      } catch (error) {
        handleWorkflowError(
          t("meetingNotes.workflow.errors.updateStatus"),
          error,
        );
        return false;
      }
    },
    [handleWorkflowError, saveMeetingAndSync, selectedMeeting, state.lists],
  );

  const handlePersonalNotesChange = useCallback(
    (notes: PersonalNote[]) => {
      setPersonalNotes(toPersonalNotesArray(notes));
    },
    [toPersonalNotesArray],
  );

  const setMeetingInReview = useCallback(async () => {
    if (!selectedMeeting) {
      return false;
    }

    const baseMeeting =
      state.lists?.find((candidate) => candidate.id === selectedMeeting.id) ||
      selectedMeeting.originalMeeting;

    if (!baseMeeting) {
      return false;
    }

    if (baseMeeting.status === MeetingStatus.InReview) {
      return true;
    }

    if (baseMeeting.status === MeetingStatus.Published) {
      return true;
    }

    return handleMeetingStatusChange(MeetingStatus.InReview, {
      inReviewOn: baseMeeting.inReviewOn || new Date(),
    });
  }, [handleMeetingStatusChange, selectedMeeting, state.lists]);

  const setMeetingToDraft = useCallback(async () => {
    if (!selectedMeeting) {
      return false;
    }

    const baseMeeting =
      state.lists?.find((candidate) => candidate.id === selectedMeeting.id) ||
      selectedMeeting.originalMeeting;

    if (!baseMeeting) {
      return false;
    }

    if (baseMeeting.status === MeetingStatus.Draft) {
      return true;
    }

    if (baseMeeting.status === MeetingStatus.Published) {
      return true;
    }

    return handleMeetingStatusChange(MeetingStatus.Draft);
  }, [handleMeetingStatusChange, selectedMeeting, state.lists]);

  const goToSelectMeeting = () => {
    setActiveStep(0);
    pendingStepRef.current = null;
    selectFirstMeeting();
    navigate("/meetings");
  };

  const goToTakeNotes = (meeting: SelectedMeeting) => {
    if (meeting.originalMeeting) {
      void actions.lists.select(meeting.originalMeeting);
    }

    setActiveStep(1);

    if (meeting.id) {
      navigate(`/meetings/workflow/${meeting.id}`);
    }
  };

  // Initialize from URL params or existing meeting
  useEffect(() => {
    let cancelled = false;

    const initializeMeeting = async () => {
      if (!meetingId) {
        setItems([]);
        setPersonalNotes([]);
        setActiveStep(0);
        return;
      }

      setItems([]);
      setPersonalNotes([]);

      let meeting = state.lists?.find(
        (candidate) => candidate.id === meetingId,
      );
      if (!meeting) {
        try {
          meeting = await actions.lists.load(meetingId);
        } catch (error) {
          handleWorkflowError(
            t("meetingNotes.workflow.errors.loadSelectedMeeting"),
            error,
          );
          return;
        }
      } else {
        void actions.lists.select(meeting);
      }

      if (!meeting || cancelled) {
        return;
      }

      // If meeting is already published, redirect to library view instead
      if (meeting.status === MeetingStatus.Published) {
        navigate(`/library/${meeting.id}/published`, { replace: true });
        return;
      }

      const nextStep = pendingStepRef.current ?? 1;
      let resolvedMeeting = meeting;

      if (nextStep >= 2 && meeting.status !== MeetingStatus.InReview) {
        try {
          resolvedMeeting = await saveMeetingAndSync({
            ...meeting,
            status: MeetingStatus.InReview,
            inReviewOn: meeting.inReviewOn || new Date(),
            updatedDate: new Date(),
          });
        } catch (error) {
          handleWorkflowError(
            t("meetingNotes.workflow.errors.moveToReview"),
            error,
          );
        }
      }

      setSelectedMeeting(meetingToSelected(resolvedMeeting));
      setActiveStep(nextStep);
      pendingStepRef.current = null;

      if (!resolvedMeeting.id) {
        setItems([]);
        setPersonalNotes([]);
        return;
      }

      try {
        const itemPromise = resolvedMeeting.items
          ? Promise.resolve(resolvedMeeting.items)
          : actions.items.list(resolvedMeeting.id);
        const notePromise = personalNoteService.getNotesForMeeting(
          resolvedMeeting.id,
        );

        const [itemsResult, notesResult] = await Promise.allSettled([
          itemPromise,
          notePromise,
        ]);

        if (cancelled) {
          return;
        }

        if (itemsResult.status === "fulfilled") {
          setItems(itemsResult.value);
        } else {
          handleWorkflowError(
            t("meetingNotes.workflow.errors.loadMeetingItems"),
            itemsResult.reason,
          );
          setItems([]);
        }

        if (notesResult.status === "fulfilled") {
          setPersonalNotes(toPersonalNotesArray(notesResult.value));
        } else {
          handleWorkflowError(
            t("meetingNotes.workflow.errors.loadPersonalNotes"),
            notesResult.reason,
          );
          setPersonalNotes([]);
        }
      } catch (error) {
        handleWorkflowError(
          t("meetingNotes.workflow.errors.initialize"),
          error,
        );
        if (!cancelled) {
          setItems([]);
          setPersonalNotes([]);
        }
      }
    };

    void initializeMeeting();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingId]);

  useEffect(() => {
    if (!meetingId && !selectedMeeting) {
      selectFirstMeeting();
    }
  }, [meetingId, selectedMeeting, selectFirstMeeting]);

  const handleSelectMeeting = (meeting: SelectedMeeting) => {
    setSelectedMeeting(meeting);
  };

  const handleGoToTakeNotes = (meeting: SelectedMeeting) => {
    setSelectedMeeting(meeting);
    goToTakeNotes(meeting);
  };

  const handleMeetingChange = (meeting: SelectedMeeting) => {
    if (selectedMeeting?.id === meeting.id) {
      setSelectedMeeting(meeting);
    }
    if (!meeting.id) {
      return;
    }

    const baseMeeting =
      meeting.originalMeeting ||
      state.lists?.find((candidate) => candidate.id === meeting.id);

    if (!baseMeeting) {
      return;
    }

    const updatedMeeting: Meeting = {
      ...baseMeeting,
      id: meeting.id,
      name: meeting.title,
      date: parseLocalDateValue(meeting.date),
      startTime: meeting.startTime || undefined,
      endTime: meeting.endTime || undefined,
    };

    void actions.lists
      .save(updatedMeeting)
      .then((savedMeeting) => {
        void actions.lists.select(savedMeeting);
        setSelectedMeeting((currentMeeting) =>
          currentMeeting?.id === savedMeeting.id
            ? meetingToSelected(savedMeeting)
            : currentMeeting,
        );
      })
      .catch((error) => {
        handleWorkflowError(
          t("meetingNotes.workflow.errors.saveMeetingDetails"),
          error,
        );
      });
  };

  const handleArrowClick = async () => {
    if (activeStep === 0 && selectedMeeting) goToTakeNotes(selectedMeeting);
    else if (activeStep === 1) {
      const proceed = async () => {
        const movedToReview = await setMeetingInReview();
        if (movedToReview) {
          setActiveStep(2);
        }
      };
      if (takeNotesRef.current) {
        takeNotesRef.current.guardNavigation(proceed);
      } else {
        await proceed();
      }
    } else if (activeStep === 2) setActiveStep(3);
    else if (activeStep === 3) distributeRef.current?.();
  };

  const handleStepClick = async (index: number) => {
    if (index === 0) {
      if (activeStep === 1 && takeNotesRef.current) {
        takeNotesRef.current.guardNavigation(goToSelectMeeting);
      } else {
        goToSelectMeeting();
      }
      return;
    }

    if (!selectedMeeting) {
      return;
    }

    if (activeStep === 0 || meetingId !== selectedMeeting.id) {
      pendingStepRef.current = index;
      goToTakeNotes(selectedMeeting);
      return;
    }

    const proceed = async () => {
      if (index >= 2) {
        const movedToReview = await setMeetingInReview();
        if (!movedToReview) {
          return;
        }
      }
      setActiveStep(index);
    };

    if (activeStep === 1 && takeNotesRef.current) {
      takeNotesRef.current.guardNavigation(proceed);
    } else {
      await proceed();
    }
  };

  //   const handleBackClick = () => {
  //     // if (activeStep === 0) {
  //     //   navigate("/events");
  //     // } else {
  //     //   // Return to the beginning of the workflow from any later step
  //     //   setActiveStep(0);
  //     // }
  //   };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <SelectMeetingStep
            selectedMeeting={selectedMeeting}
            onSelectMeeting={handleSelectMeeting}
            onGoToTakeNotes={handleGoToTakeNotes}
            onMeetingChange={handleMeetingChange}
            onError={handleWorkflowError}
          />
        );
      case 1:
        return (
          <TakeNotesStep
            ref={takeNotesRef}
            onNext={() => setActiveStep(2)}
            onBack={goToSelectMeeting}
            items={items}
            onItemsChange={setItems}
            selectedMeeting={selectedMeeting}
            onMeetingChange={handleMeetingChange}
            onMeetingStatusChange={handleMeetingStatusChange}
            personalNotes={safePersonalNotes}
            onPersonalNotesChange={handlePersonalNotesChange}
            onError={handleWorkflowError}
          />
        );
      case 2:
        return (
          <ReviewStep
            onNext={() => setActiveStep(3)}
            onBack={() => setActiveStep(1)}
            items={items}
            onItemsChange={setItems}
            selectedMeeting={selectedMeeting}
            onMeetingChange={handleMeetingChange}
            personalNotes={safePersonalNotes}
            onError={handleWorkflowError}
          />
        );
      case 3:
        return (
          <DistributeStep
            onBack={() => setActiveStep(2)}
            onDistributeComplete={() =>
              navigate(`/library/${selectedMeeting?.id}/published`)
            }
            selectedMeeting={selectedMeeting}
            items={items}
            distributeRef={distributeRef}
            onMeetingChange={handleMeetingChange}
            onMeetingStatusChange={handleMeetingStatusChange}
            onError={handleWorkflowError}
          />
        );
      default:
        return null;
    }
  };

  const isArrowDisabled = activeStep === 0 && !selectedMeeting;

  return (
    <Box
      sx={{
        height: "100%",
        backgroundColor: "#FAFBFC",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Header with Stepper */}
      <Box
        sx={{
          borderBottom: "1px solid #E5E7EB",
          backgroundColor: "#FFFFFF",
          px: 3,
          py: 1.5,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            maxWidth: "1200px",
            mx: "auto",
            display: "flex",
            alignItems: "center",
          }}
        >
          {/* Stepper */}
          <Box sx={{ flex: 1 }}>
            <Stepper
              activeStep={activeStep}
              nonLinear
              connector={<WorkflowConnector />}
              sx={{
                "& .MuiStepConnector-root": {
                  top: 10,
                },
              }}
            >
              {steps.map((label, index) => (
                <Step key={label} completed={index < activeStep}>
                  <StepButton
                    onClick={() => handleStepClick(index)}
                    disabled={index > 0 && !selectedMeeting}
                    sx={{ py: 0.5 }}
                  >
                    {label}
                  </StepButton>
                </Step>
              ))}
            </Stepper>
          </Box>

          {/* Forward arrow */}
          <Tooltip title={arrowLabels[activeStep] || ""} arrow>
            <Box component="span" sx={{ display: "inline-flex", ml: 3 }}>
              <IconButton
                onClick={handleArrowClick}
                disabled={isArrowDisabled}
                sx={[{
                  color: "#FFFFFF",
                  width: 36,
                  height: 36,

                  "&.Mui-disabled": {
                    color: "#FFFFFF",
                    backgroundColor: "#D1D5DB",
                  }
                }, isArrowDisabled ? {
                  backgroundColor: "#9CA3AF"
                } : {
                  backgroundColor: "#242C63"
                }, isArrowDisabled ? {
                  "&:hover": {
                    backgroundColor: "#9CA3AF"
                  }
                } : {
                  "&:hover": {
                    backgroundColor: "#353D7A"
                  }
                }]}
              >
                <ArrowForwardIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>
          </Tooltip>
        </Box>
      </Box>
      {/* Main content */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          ...hiddenScrollbarSx,
        }}
      >
        <Box sx={{ maxWidth: "1200px", mx: "auto", px: 3, py: 4 }}>
          {renderStepContent()}
        </Box>
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MeetingWorkflow;
